<script>
  import {
    editingMode,
    isTouch,
    preferences,
    projectStore,
    readingNovel,
    readonly,
    selectedCount,
    settings,
  } from '@dabble/app';
  import { countWords } from '@dabble/data/word-count';
  import TitleImage from '@dabble/plugins/content/components/TitleImage.svelte';
  import ContextMenu from '@dabble/toolkit/ContextMenu.svelte';
  import EditableContextMenu from '@dabble/toolkit/EditableContextMenu.svelte';
  import LineMenu from '@dabble/toolkit/LineMenu.svelte';
  import TextBubbleMenu from '@dabble/toolkit/TextBubbleMenu.svelte';
  import { tooltipTop } from '@dabble/toolkit/tooltip';
  import { fadeUp } from '@dabble/toolkit/transitions';
  import { onDestroy, onMount, tick } from 'svelte';
  import { Delta, Source, TextDocument } from 'typewriter-editor';
  import { editables } from './editables';

  export let doc;
  export let field;
  export let header = null;
  export let typeset = null;
  export let onSave = undefined;
  export let hiddenPlaceholder = null;
  export let placeholder = null;
  export let isEmpty = true;
  export let prefix = false;
  export let prefixClick = null;
  export let prefixTooltip = null;
  let fieldFocus = false;
  export { fieldFocus as focus };
  let className = '';
  export { className as class };
  let fieldReadonly = false;
  export { fieldReadonly as readonly };
  export let editable = null;
  export let showTitleImages = true;

  let docId;
  let root;
  let unsubscribes = [];
  let emptyUnsubscribe;

  $: docId = doc && doc.id;
  $: onDocIdChange(docId, root);
  $: onReadonlyChange($readonly, fieldReadonly);
  $: $settings && rerender();

  onMount(() => {
    unsubscribes = [projectStore.forceTextUpdate(onForceTextUpdate)];
  });

  onDestroy(() => {
    unsubscribes.forEach(unsub => unsub());
    if (editable) editable.destroy();
  });

  function onDocIdChange(docId) {
    if (editable) {
      editable.editor.off('change', onChange);
      if (emptyUnsubscribe) {
        emptyUnsubscribe();
      }
      editable.destroy();
      editable = null;
      if (root) root.innerHTML = '';
    }

    if (root && docId) {
      const options = { header, onSave, placeholder };
      if ($readonly || fieldReadonly) options.enabled = false;
      if (typeset) options.typeset = typeset;
      editable = editables.getEditable(docId, field, root, options);
      emptyUnsubscribe = editable.empty.subscribe(value => (isEmpty = value));
      editable.editor.on('change', onChange);
      if (fieldFocus) Promise.resolve().then(() => editable.editor.select(0));
    }
  }

  async function rerender() {
    await tick();
    if (editable) editable.editor.render();
  }

  function onChange({ doc: { selection }, change, source }) {
    if (!header) {
      if (!selection) {
        selectedCount.set(0);
      } else {
        const text = editable.editor.doc.getText(selection);
        const numWords = countWords(text);
        selectedCount.set((text && numWords) || 0);
      }
    }
    if (
      change &&
      change.contentChanged &&
      (source === 'user' || source === 'input') &&
      change.delta.ops.some(op => op.delete || typeof op.insert === 'string')
    ) {
      root.dispatchEvent(
        new CustomEvent('editorinput', { detail: { editor: editable.editor, change }, bubbles: true })
      );
    }
  }

  function onReadonlyChange() {
    if (editable) editable.editor.enabled = !($readonly || fieldReadonly);
  }

  function onForceTextUpdate(key) {
    if (!editable || (key && key !== editable.key)) return;
    const { editor } = editable;
    const queued = projectStore.textQueue.getFor(projectStore.getQueueKey(doc.id, field));

    if (header || !doc[field]) {
      let text = doc[field] || '';
      if (queued) {
        text = new Delta()
          .insert(text)
          .compose(queued.delta)
          .filter(op => typeof op.insert === 'string')
          .map(op => op.insert)
          .join('');
      }

      if (editor.getText() !== text) {
        editor.setText(text, editor.selection, Source.api);
      } else {
        editor.render();
      }
    } else {
      let textDoc = doc[field] || new TextDocument();
      if (queued) {
        textDoc = textDoc.apply(queued.delta);
      }

      if (!textDoc.equals(editor.doc, { contentOnly: true })) {
        editor.set(textDoc, Source.api);
      } else {
        editor.render();
      }
    }
  }
</script>

<div
  data-id={doc && doc.id}
  data-field={field}
  class="editable-content {className} {!placeholder && settings.getPlaceholderClass(doc, field, isEmpty)}"
  class:empty={isEmpty}
  class:hidden-placeholder={hiddenPlaceholder}
  class:has-image={!!doc.image}
>
  <div bind:this={root} />
  {#if editable}
    {#if !isEmpty && prefix}
      <div
        transition:fadeUp|local={{ distance: 100 }}
        class="title-prefix"
        class:clickable={prefixClick}
        on:click={prefixClick}
        on:keydown={prefixClick}
        use:tooltipTop={prefixTooltip}
      >
        {($projectStore.project && settings.getPlaceholder(doc)) || ''}
      </div>
    {/if}
  {/if}

  {#if doc.image && doc.type === 'novel_chapter' && showTitleImages}
    <TitleImage {doc} />
  {/if}

  {#if !header}
    {#if !$readingNovel}
      <TextBubbleMenu editor={editable && editable.editor} />
    {/if}
    {#each settings.getValuesFromPlugins('editableMenus', $projectStore.project) as menu}
      <svelte:component this={menu} editor={editable && editable.editor} {doc} />
    {/each}
  {/if}

  {#if $$slots.lineMenuItems && editingMode !== 1}
    <LineMenu editor={editable && editable.editor} let:commands let:active let:selection let:focus>
      <svelte:fragment slot="lineMenuItems">
        <slot name="lineMenuItems" {commands} {active} {selection} {focus} editor={editable && editable.editor} />
      </svelte:fragment>
    </LineMenu>
  {/if}

  {#if !$isTouch && !$preferences.preferNativeContextMenu}
    <ContextMenu activeClass="menu-open" target={root}>
      <EditableContextMenu editor={editable && editable.editor} />
    </ContextMenu>
  {/if}
</div>

<style>
  .clickable {
    cursor: pointer;
  }
</style>
